import React, {
  createContext,
  useContext,
  type ReactNode,
} from "react";
import type { CompanyEmployeeSearchOption } from "@/hooks/useCompanyEmployeeSearch";
import { useShellEmployeeSearch } from "@/contexts/ShellEmployeeSearchContext";

type ShellEmployeeSelectionSource =
  | "shell_search"
  | "timesheets"
  | "disputes"
  | "manual_entry"
  | "preflight"
  | "employee_record"
  | "unknown";

export type ShellEmployeeSelectionState = {
  activeEmployeeId: string | null;
  activeEmployee: CompanyEmployeeSearchOption | null;
  activeEmployeeSource: ShellEmployeeSelectionSource;
  setActiveEmployeeId: (
    employeeId: string | null,
    source?: ShellEmployeeSelectionSource
  ) => void;
  clearActiveEmployee: () => void;
};

const ShellEmployeeSelectionContext =
  createContext<ShellEmployeeSelectionState | null>(null);

export function ShellEmployeeSelectionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const employeeSearch = useShellEmployeeSearch();

  const [activeEmployeeId, setActiveEmployeeIdState] =
    React.useState<string | null>(null);

  const [activeEmployeeSource, setActiveEmployeeSource] =
    React.useState<ShellEmployeeSelectionSource>("unknown");

  const activeEmployee =
    activeEmployeeId == null
      ? null
      : employeeSearch.options.find(
          (option) => option.employeeId === activeEmployeeId
        ) ??
        employeeSearch.filteredOptions.find(
          (option) => option.employeeId === activeEmployeeId
        ) ??
        null;

  function setActiveEmployeeId(
    employeeId: string | null,
    source: ShellEmployeeSelectionSource = "unknown"
  ) {
    setActiveEmployeeIdState(employeeId);
    setActiveEmployeeSource(employeeId ? source : "unknown");
  }

  function clearActiveEmployee() {
    setActiveEmployeeIdState(null);
    setActiveEmployeeSource("unknown");
  }

  return (
    <ShellEmployeeSelectionContext.Provider
      value={{
        activeEmployeeId,
        activeEmployee,
        activeEmployeeSource,
        setActiveEmployeeId,
        clearActiveEmployee,
      }}
    >
      {children}
    </ShellEmployeeSelectionContext.Provider>
  );
}

export function useShellEmployeeSelection(): ShellEmployeeSelectionState {
  const context = useContext(ShellEmployeeSelectionContext);

  if (!context) {
    throw new Error(
      "useShellEmployeeSelection must be used within ShellEmployeeSelectionProvider."
    );
  }

  return context;
}